//--------------------------------------------------------------------------------------//
//                       TANSTACK QUERY PROVIDER IMPLEMENTATION                         //
//--------------------------------------------------------------------------------------//

import { DatabasesAPI, TableColummnsAPI, TableDataAPI, TablesAPI } from '@makyoapp/makyo-sdk'
import type { ITable, ITableRow, Row } from '@makyoapp/makyo-sdk/models/Table'

import { queryClient, queryKeys } from '@/lib/react-query'

import { IDBProvider } from './provider'

const CACHE_TIME = 1000 * 60 * 60 // 1 hour

export const TanstackQueryProvider = {
  // ======================= DB Methods =======================
  getDB: (dbId) => {
    return queryClient.fetchQuery({
      queryKey: [queryKeys.Database, dbId],
      queryFn: () => DatabasesAPI.ReqGet(dbId),
      staleTime: CACHE_TIME,
    })
  },
  updateDb: async (dbId, data) => {
    await DatabasesAPI.ReqUpdateSettings(dbId, data)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Database, dbId] })
  },
  async renameDB(dbId, name) {
    await DatabasesAPI.ReqRename(dbId, name)

    return Promise.resolve(null)
  },
  async deleteDB(dbId) {
    await DatabasesAPI.ReqDelete(dbId)

    queryClient.removeQueries({ queryKey: [queryKeys.Database] })
  },
  listTables(dbId) {
    const tables = queryClient.fetchQuery({
      queryKey: [queryKeys.Tables, dbId],
      queryFn: () => TablesAPI.ReqList(dbId, ''),
      staleTime: CACHE_TIME,
    })

    return tables
  },

  // ======================= Table Methods =======================
  async renameTable(tableId, newName, dbId) {
    const response = await TablesAPI.ReqRename(tableId, newName)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })

    return response
  },
  async deleteTable(tableId, dbId) {
    const response = await TablesAPI.ReqDelete(tableId)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })
    return response
  },
  async createTable(tableName, dbId) {
    const response = await TablesAPI.ReqAdd(tableName, dbId)

    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })
    return response
  },
  async reorderTable(dbId, tablesId) {
    await TablesAPI.ReqReorder(dbId, tablesId)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })
  },
  getTableData(tableID, lang, userSpecific) {
    return queryClient.fetchQuery({
      queryKey: [queryKeys.TableData, { tableID, lang, userSpecific }],
      queryFn: () => TableDataAPI.ReqListWithInfiniteScroll(tableID, lang, userSpecific),
      staleTime: CACHE_TIME,
    })
  },
  async updateTimezone(dbId, tableID, timezone) {
    await TablesAPI.ReqSaveTz(tableID, timezone)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })
  },

  // ======================= Snapshot Table =======================
  async createSnapshotTable(tableName, dbId, settings) {
    const response = await TablesAPI.ReqAddSnapshot(tableName, dbId, settings)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })
    return response
  },

  async updateSnapshotTable(tableId, settings) {
    await TablesAPI.ReqUpdateSnapshotSettings(tableId, settings)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, tableId] })
  },

  // ======================= Query Table =======================
  async createQueryTable(tableName, dbId, settings) {
    const response = await TablesAPI.ReqAddQuery(tableName, dbId, settings)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })
    return response
  },

  async updateQueryTable(tableId, settings) {
    await TablesAPI.ReqUpdateQuerySettings(tableId, settings)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, tableId] })
  },

  async duplicateQueryTable(tableName, tableId, dbId) {
    const response = await TablesAPI.ReqDuplicateQuery(tableId, tableName)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })
    return response
  },

  // ======================= Row Methods =======================
  async updateRows(tableID, rows, lang, userSpecific) {
    await TableDataAPI.ReqUpdateManyRows(tableID, rows, lang, userSpecific)
  },
  async duplicateRows(tableID, rowIds, lang, userSpecific) {
    const rows = await TableDataAPI.ReqDuplicateManyRows(tableID, rowIds)
    return rows as ITableRow[]
  },
  async addRow(tableID, newRow, lang, userSpecific) {
    const response = await TableDataAPI.ReqAdd(tableID, lang, newRow as Row, userSpecific)
    return response
  },
  async appendRows(tableID, rows, lang, userSpecific) {
    // Batch add the rows to the backend
    const response = await TableDataAPI.ReqAddMany(tableID, lang, rows, userSpecific)
    return response
  },
  async deleteRows(tableID, ids) {
    await TableDataAPI.ReqDeleteManyRows(tableID, ids)
  },
  async clearRows(tableId) {
    await TableDataAPI.ReqClearRows(tableId)
  },

  // ======================= Column Methods =======================
  async updateColumnOrder(tableID, newColumnsOrder, dbId) {
    await TableColummnsAPI.ReqReorder(tableID, newColumnsOrder)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })
  },
  async updateColumnSettings(tableId, columnId, body, dbId) {
    await TableColummnsAPI.ReqUpdate(tableId, columnId, body)

    // Optimistically update the React Query cache
    queryClient.setQueryData([queryKeys.Tables, dbId], (prevTables: ITable[]) => {
      let updatedTableIdx = (prevTables || []).findIndex((tb) => tb._id === tableId)

      if (updatedTableIdx > -1) {
        let prevColumns = prevTables[updatedTableIdx]._Columns
        let updatedColumnIdx = prevColumns.findIndex((c) => c._id === columnId)
        if (updatedColumnIdx > -1) {
          prevColumns[updatedColumnIdx] = {
            ...prevColumns[updatedColumnIdx],
            ...body,
          }
          prevTables[updatedTableIdx]._Columns = prevColumns
        }
      }
      return prevTables
    })
  },
  async addColumn(tableID, columnBody, dbId) {
    const response = await TableColummnsAPI.ReqAdd(tableID, columnBody)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })

    return response
  },
  async deleteColumn(tableID, columnId, dbId) {
    await TableColummnsAPI.ReqDelete(tableID, columnId)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })
  },
  async updateColumn(tableId, column, dbId) {
    await TableColummnsAPI.ReqUpdate(tableId, column._id, column)

    // Optimistically update the React Query cache
    queryClient.setQueryData([queryKeys.Tables, dbId], (prevTables: ITable[]) => {
      let updatedTableIdx = (prevTables || []).findIndex((tb) => tb._id === tableId)

      if (updatedTableIdx > -1) {
        let prevColumns = prevTables[updatedTableIdx]._Columns
        let updatedColumnIdx = prevColumns.findIndex((c) => c._id === column._id)
        if (updatedColumnIdx > -1) {
          prevColumns[updatedColumnIdx] = {
            ...prevColumns[updatedColumnIdx],
            ...column,
          }
          prevTables[updatedTableIdx]._Columns = prevColumns
        }
      }
      return prevTables
    })
  },
  async freezeColumn(tableID, columnID, body, dbId) {
    await TableColummnsAPI.ReqFreeze(tableID, columnID, body)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })
  },
  async hideColumn(tableId, columnId, dbId, isHidden) {
    await TableColummnsAPI.ReqHide(tableId, columnId, { _IsHidden: isHidden })

    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })
  },
  async duplicateColumn(tableID, columnID, dbId, newColumnName) {
    const response = await TableColummnsAPI.ReqDuplicate(tableID, columnID, {
      _Name: newColumnName,
    })

    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })
    return response
  },
  async insertColumn(tableID, targetColumnID, position, newColumnBody, dbId) {
    const response = await TableColummnsAPI.ReqInsert(tableID, targetColumnID, position, newColumnBody)
    queryClient.invalidateQueries({ queryKey: [queryKeys.Tables, dbId] })

    return response
  },
} satisfies IDBProvider
